import React from "react";
import { connect, ConnectedProps } from "react-redux";
import { RootState } from "../store/store";
import jsPDF from "jspdf";
import {
  Button,
  DatePicker,
  Drawer,
  Form,
  Input,
  Space,
  Table,
  Descriptions,
  Spin,
} from "antd";
import { AppstoreOutlined, TableOutlined } from "@ant-design/icons";
import { useState } from "react";
import { PATH_EXPENSE as PATH, PATH_MANAGE } from "../config";
import CustomPaginatedTable from "../components/CustomPaginatedTable";

import { isArray } from "lodash";
import dayjs from "dayjs";
import { queryClient } from "../store/queryClient";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryApi } from "../store/actions/common";
import { triggerNotification } from "../store/directActions";
import moment from "moment";
import { MetricCard } from "../components/MetricCard";
import PageHeader from "../components/PageHeader";
import countMatches from "../utils/countMatches";
import sortBySoldClosed from "../utils/sortBySoldClosed";
import activatedToday from "../utils/activatedToday";
import { useLocalState } from "../hooks/useLocalStorage";
const { RangePicker } = DatePicker;
const dateFormat = "YYYY/MM/DD";
const VIEW_MODE_STORAGE_KEY = "shared-page-view-mode";

interface OwnProps {}

type configurationPageProps = OwnProps & PropsFromRedux;

const PAGE_TYPE = "expense";

const ConfigurationPage: React.FC<configurationPageProps> = ({
  data,
  storeId,
  fetchCms,
  createData,
  editData,
  REFRESH,
}) => {
  const [showDrawer, setShowDrawer] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [viewMode, setViewMode] = useLocalState(VIEW_MODE_STORAGE_KEY, "card");

  const [filterData, setFilterData] = useState({
    start_date: dayjs().startOf("month").format("YYYY-MM-DDTHH:mm:ss"),
    end_date: dayjs().add(1, "day").endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
  });
  const [stats, setStats] = useState<any>({
    instant_sales: 0,
    instant_cashes: 0,
    online_sales: 0,
    online_cashes: 0,
    reg_cash: 0,
  });

  const getDataStats = (data: any[]) => {
    if (isArray(data)) {
      let instant_sales = 0;
      let instant_cashes = 0;
      let online_sales = 0;
      let online_cashes = 0;
      let reg_cash = 0;

      data.map((item) => {
        // if(is_already.some(i => i === item.date)){
        //   return;
        // }

        instant_sales = instant_sales + item.instant_sales;
        instant_cashes = instant_cashes + item.instant_cashes;
        online_sales = online_sales + item.online_sales;
        online_cashes = online_cashes + item.online_cashes;
        reg_cash =
          reg_cash +
          (item.instant_sales -
            item.online_cancel +
            item.online_sales -
            item.online_cashes -
            item.instant_cashes);
      });

      setStats({
        instant_sales,
        instant_cashes,
        online_sales,
        online_cashes,
        reg_cash,
      });
    }
  };

  const [form] = Form.useForm();

  const updateDrawer = (state: boolean = false) => {
    setShowDrawer(state);
    if (!state) {
      setIsEditMode(false);
      setSelectedId(null);
      form.resetFields();
    }
  };

  // Query to fetch report details by ID
  const {
    data: reportDetail,
    isPending: isDetailPending,
    refetch: refetchDetail,
  } = useQuery({
    queryKey: ["report/dayclose", selectedId],
    queryFn: () => queryApi(`report/dayclose/${selectedId}`, "GET"),
    enabled: !!selectedId && showDrawer,
    staleTime: Infinity,
    gcTime: 0,
  });

  // Mutation to update report details
  const { mutate: updateReport, isPending: isUpdating } = useMutation({
    mutationFn: ({ data, id }: any) =>
      queryApi(`report/dayclose/${id}`, "PUT", data),
    onSuccess: () => {
      setIsEditMode(false);
      queryClient.invalidateQueries({
        queryKey: ["report/dayclose"],
      });
      refetchDetail();
      triggerNotification({
        title: "Success",
        description: "Report updated successfully",
        type: "success",
      });
    },
    onError: (error: any) => {
      triggerNotification({
        title: "Error",
        description: error.message || "Failed to update report",
        type: "error",
      });
    },
  });

  const onFormSubmit = (values: Record<string, any>) => {
    if (selectedId) {
      updateReport({ data: values, id: selectedId });
    }
  };

  const handleViewDetail = (record: any) => {
    setSelectedId(record.id);
    setShowDrawer(true);
    setIsEditMode(false);
  };

  const customRender = (item: any, index: number, globalIndex: number) => {
    const onlineSales = item.online_sales - item.online_cancel;
    const regCash =
      item.instant_sales -
      item.online_cancel +
      item.online_sales -
      item.online_cashes -
      item.instant_cashes;

    return (
      <div
        key={item.id || index}
        className="app-surface rounded-xl shadow-sm border border-solid app-border p-6 hover:shadow-md transition-shadow duration-200 mb-4"
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="inline-flex items-center px-3 py-1 bg-blue-100 dark:bg-blue-900/30 rounded-lg mb-2">
              <span className="text-lg font-bold text-blue-600 dark:text-blue-400">
                {moment(item.date).format("MMMM DD, YYYY")}
              </span>
            </div>
            <p className="text-sm app-muted">
              Report #{index + 1}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <p className="text-sm app-muted">
              Instant Sales
            </p>
            <p
              className="text-lg font-semibold"
              style={{ color: "var(--dash-value)" }}
            >
              ${item.instant_sales}
            </p>
          </div>
          <div>
            <p className="text-sm app-muted">
              Instant Cashes
            </p>
            <p className="text-lg font-semibold app-text">
              ${item.instant_cashes}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <p className="text-sm app-muted">
              Online Sales
            </p>
            <p className="text-lg font-semibold text-blue-600">
              ${onlineSales}
            </p>
          </div>
          <div>
            <p className="text-sm app-muted">
              Online Cashes
            </p>
            <p className="text-lg font-semibold app-text">
              ${item.online_cashes}
            </p>
          </div>
        </div>

        <div className="mb-4">
          <p className="text-sm app-muted">
            Cash In Reg
          </p>
          <p className="text-xl font-bold text-purple-600 dark:text-purple-400">
            ${regCash}
          </p>
        </div>

        <div className="flex justify-end items-center pt-4 border-0 border-t border-solid app-border">
          <div
            onClick={() => handleViewDetail(item)}
            className="text-sm font-medium cursor-pointer"
            style={{ color: "var(--dash-link)" }}
          >
            View Detail
          </div>
        </div>
      </div>
    );
  };

  // Set form values when report detail is loaded
  React.useEffect(() => {
    if (reportDetail) {
      form.setFieldsValue({
        online_sales: reportDetail.online_sales,
        instant_cashes: reportDetail.instant_cashes,
        online_cashes: reportDetail.online_cashes,
        instant_sales: reportDetail.instant_sales,
        online_cancel: reportDetail.online_cancel,
      });
    }
  }, [reportDetail, form]);

  const generatePDF = (date: string) => {
    const input = document.getElementById("pdf-element");
    if (input) {
      const pdf = new jsPDF({ unit: "px", format: "letter", userUnit: "px" });
      pdf
        .html(input as HTMLElement, { html2canvas: { scale: 0.5 } })
        .then(() => {
          pdf.save(`report_${date}.pdf`);
        });
    }
  };

  const computeRegCash = (r: any) =>
    r.instant_sales -
    r.online_cancel +
    r.online_sales -
    r.online_cashes -
    r.instant_cashes;

  const columns = [
    {
      title: "Date Created",
      dataIndex: "date",
      key: "date",
      sorter: (a: any, b: any) =>
        moment(a.date).valueOf() - moment(b.date).valueOf(),
      render: (item, record, index) => (
        <div>{moment(item).format("MMMM DD, YYYY")}</div>
      ),
    },

    {
      title: "Insant Sales",
      dataIndex: "instant_sales",
      key: "instant_sales",
      sorter: (a: any, b: any) =>
        (a.instant_sales ?? 0) - (b.instant_sales ?? 0),
      render: (item, record, index) => <div>{record.instant_sales}</div>,
    },
    {
      title: "Insant Cashes",
      dataIndex: "instant_cashes",
      key: "instant_cashes",
      sorter: (a: any, b: any) =>
        (a.instant_cashes ?? 0) - (b.instant_cashes ?? 0),
    },
    {
      title: "Online Sales",
      dataIndex: "online_sales",
      key: "online_sales",
      sorter: (a: any, b: any) =>
        a.online_sales - a.online_cancel - (b.online_sales - b.online_cancel),
      render: (item, record, index) => (
        <div>{record.online_sales - record.online_cancel}</div>
      ),
    },

    {
      title: "Online Cashes",
      dataIndex: "online_cashes",
      key: "online_cashes",
      sorter: (a: any, b: any) =>
        (a.online_cashes ?? 0) - (b.online_cashes ?? 0),
    },
    // {
    //   title: "Online Cancel",
    //   dataIndex: "online_cancel",
    //   key: "online_cancel",
    // },
    {
      title: "Cash In Reg",
      dataIndex: "reg_cash",
      key: "reg_cash",
      sorter: (a: any, b: any) => computeRegCash(a) - computeRegCash(b),
      render: (item, record, index) => <div>{computeRegCash(record)}</div>,
    },
    // {
    //   title: "Date Added",
    //   dataIndex: "updated_at",
    //   key: "updated_at",
    // },

    // {
    //   title: "Close",
    //   key: "is_closed",
    //   render: (item, record, index) => (
    //     <Space size="middle">
    //       <Switch
    //         checked={item.is_closed}
    //         onChange={() => onUpdateItem(item,{is_closed: true})}
    //       />
    //     </Space>
    //   ),
    // },

    {
      title: "Action",
      key: "action",
      render: (record: any) => (
        <Space size="middle">
          <a
            onClick={() => handleViewDetail(record)}
            style={{ color: "var(--dash-link)" }}
          >
            View Detail
          </a>
        </Space>
      ),
    },
  ];

  const computeShiftTotal = (record: any) => {
    const sold = record.is_sold === 1 ? 1 : 0;
    return record.is_reverse === 0
      ? (record.current_no - record.start_no + sold) * record.lottery?.amount
      : (record.start_no - record.current_no + sold) * record.lottery?.amount;
  };

  // End shift columns for detail drawer
  const endShiftColumns = [
    {
      title: <div style={{ minWidth: "max-content" }}>Box #</div>,
      dataIndex: "slot_number",
      key: "slot_number",
      render: (slot_number: any) => <div>#{slot_number}</div>,
      width: 70,
      sorter: (a: any, b: any) => (a.slot_number ?? 0) - (b.slot_number ?? 0),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a: any, b: any) =>
        (a.lottery?.name ?? "").localeCompare(b.lottery?.name ?? ""),
      render: (item: any, record: any) => <div>{record.lottery?.name}</div>,
      width: 240,
    },
    {
      title: <div style={{ minWidth: "max-content" }}>Game-Pack #</div>,
      dataIndex: "game",
      key: "game",
      sorter: (a: any, b: any) =>
        (a.lottery?.game_number ?? 0) - (b.lottery?.game_number ?? 0) ||
        (a.pack ?? 0) - (b.pack ?? 0),
      render: (item: any, record: any) => (
        <div style={{ minWidth: "max-content" }}>
          {record.lottery?.game_number}-{record.pack}
        </div>
      ),
      width: 120,
    },
    {
      title: <div style={{ minWidth: "max-content" }}>Start #</div>,
      dataIndex: "start_no",
      key: "start_no",
      width: 100,
      sorter: (a: any, b: any) => (a.start_no ?? 0) - (b.start_no ?? 0),
    },
    {
      title: <div style={{ minWidth: "max-content" }}>End #</div>,
      dataIndex: "current_no",
      key: "current_no",
      width: 100,
      sorter: (a: any, b: any) => (a.current_no ?? 0) - (b.current_no ?? 0),
    },
    {
      title: "Value",
      dataIndex: "lottery",
      key: "lottery",
      sorter: (a: any, b: any) =>
        (a.lottery?.amount ?? 0) - (b.lottery?.amount ?? 0),
      render: (item: any) => (
        <div>
          {item?.currency}
          {item?.amount}
        </div>
      ),
      width: 100,
    },
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
      sorter: (a: any, b: any) => computeShiftTotal(a) - computeShiftTotal(b),
      render: (item: any, record: any) => (
        <div>
          {record.lottery?.currency}
          {computeShiftTotal(record)}
        </div>
      ),
      width: 100,
    },
    {
      title: "Closing Status",
      key: "is_sold",
      render: (is_sold: any, record: any) => (
        <Space size="middle">
          {record.is_paused === 1 ? (
            <div>Paused</div>
          ) : record.is_sold === 1 ? (
            <div>Sold</div>
          ) : record.is_closed === 1 ? (
            <div>Closed</div>
          ) : (
            <div>Active</div>
          )}
        </Space>
      ),
    },
  ];
  return (
    <section id="customers" className="page-section h-page p-6 flex flex-col">
      <PageHeader
        title=""
        extra={[
          <RangePicker
            key="date-picker"
            defaultValue={[
              dayjs().startOf("month"),
              dayjs().endOf("day").endOf("day"),
            ]}
            format={dateFormat}
            onChange={(e) => {
              if (e)
                setFilterData({
                  start_date: e[0]?.format("YYYY-MM-DDTHH:mm:ss"),
                  end_date: e[1]
                    ?.add(1, "day")
                    .endOf("day")
                    .format("YYYY-MM-DDTHH:mm:ss"),
                });
            }}
          />,
          <Button.Group key="view-toggle">
            <Button
              type={viewMode === "table" ? "primary" : "default"}
              icon={<TableOutlined />}
              onClick={() => setViewMode("table")}
            >
              Table
            </Button>
            <Button
              type={viewMode === "card" ? "primary" : "default"}
              icon={<AppstoreOutlined />}
              onClick={() => setViewMode("card")}
            >
              Card
            </Button>
          </Button.Group>,
        ]}
      />

      <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-4">
        <MetricCard
          title="Instant Sales"
          value={`$${stats?.instant_sales}`}
          accent
        />
        <MetricCard
          title="Instant Cashes"
          value={`$${stats?.instant_cashes}`}
          accent
        />
        <MetricCard
          title="Online Sales"
          value={`$${stats?.online_sales}`}
          accent
        />
        <MetricCard
          title="Online Cashes"
          value={`$${stats?.online_cashes}`}
          accent
        />
        <MetricCard
          title="Reg Cash"
          value={`$${stats?.reg_cash}`}
          accent
        />
      </div>

      {/* @ts-ignore */}
      <CustomPaginatedTable
        columns={columns}
        hidePagination={true}
        filterData={filterData}
        getDataStats={getDataStats}
        queryKey={["report/dayclose", { type: "all" }]}
        path={"report/dayclose"}
        customRender={viewMode === "card" ? customRender : null}
        customWrapperClass={
          viewMode === "card"
            ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            : undefined
        }
        tableProps={
          viewMode === "table"
            ? {
                sticky: true,
                tableLayout: "auto",
                scroll: { y: true, x: true },
              }
            : undefined
        }
      />

      <Drawer
        title="Report Details"
        width={1000}
        onClose={() => updateDrawer()}
        open={showDrawer}
        footer={
          <div style={{ textAlign: "right" }}>
            {!isEditMode && (
              <Button
                type="primary"
                onClick={() => setIsEditMode(true)}
                style={{ marginRight: 8 }}
                disabled={isDetailPending}
              >
                Edit
              </Button>
            )}
            {isEditMode && (
              <Button
                type="primary"
                onClick={() => form.submit()}
                style={{ marginRight: 8 }}
                loading={isUpdating}
              >
                Save Changes
              </Button>
            )}
            <Button
              type="primary"
              onClick={() =>
                generatePDF(moment(reportDetail?.date).format("MM_DD_YYYY"))
              }
              style={{ marginRight: 8 }}
              disabled={isDetailPending || !reportDetail}
            >
              Download Report
            </Button>
            <Button onClick={() => updateDrawer()}>Close</Button>
          </div>
        }
      >
        {isDetailPending ? (
          <div className="w-full h-full flex items-center justify-center">
            <Spin />
          </div>
        ) : (
          <div
            id="pdf-element"
            style={{ display: "flex", flexDirection: "column", height: "100%" }}
          >
            <Form
              form={form}
              onFinish={onFormSubmit}
              hideRequiredMark
              style={{ width: "100%" }}
            >
              <Descriptions bordered size={"small"} column={4}>
                <Descriptions.Item label="Report Date" span={3}>
                  {moment(reportDetail?.date).format("MMMM DD, YYYY")}
                </Descriptions.Item>
                <Descriptions.Item label="Instant Sales" span={3}>
                  {!isEditMode ? (
                    <>${reportDetail?.instant_sales} </>
                  ) : (
                    <Form.Item
                      name="instant_sales"
                      noStyle
                      rules={[
                        {
                          required: true,
                          message: "Please enter instant sales",
                        },
                      ]}
                    >
                      <Input
                        placeholder="Instant Sales"
                        bordered={false}
                        readOnly
                        value={reportDetail?.instant_sales}
                        style={{ padding: 0 }}
                      />
                    </Form.Item>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Online Sales" span={3}>
                  {!isEditMode ? (
                    <>
                      $
                      {reportDetail?.online_sales -
                        reportDetail?.online_cancel}{" "}
                    </>
                  ) : (
                    <Form.Item
                      name="online_sales"
                      noStyle
                      rules={[
                        {
                          required: true,
                          message: "Please enter online sales",
                        },
                      ]}
                    >
                      <Input
                        placeholder="Online Sales"
                        bordered={false}
                        value={
                          reportDetail?.online_sales -
                          reportDetail?.online_cancel
                        }
                        style={{ padding: 0 }}
                      />
                    </Form.Item>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Instant Cashes" span={3}>
                  {!isEditMode ? (
                    <>${reportDetail?.instant_cashes} </>
                  ) : (
                    <Form.Item
                      name="instant_cashes"
                      noStyle
                      rules={[
                        {
                          required: true,
                          message: "Please enter instant cashes",
                        },
                      ]}
                    >
                      <Input
                        placeholder="Instant cashes"
                        bordered={false}
                        value={reportDetail?.instant_cashes}
                        style={{ padding: 0 }}
                      />
                    </Form.Item>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Online Cashes" span={3}>
                  {!isEditMode ? (
                    <>${reportDetail?.online_cashes} </>
                  ) : (
                    <Form.Item
                      name="online_cashes"
                      noStyle
                      rules={[
                        {
                          required: true,
                          message: "Please enter online cashes",
                        },
                      ]}
                    >
                      <Input
                        placeholder="Online Cashes"
                        bordered={false}
                        value={reportDetail?.online_cashes}
                        style={{ padding: 0 }}
                      />
                    </Form.Item>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Online Cancel" span={3}>
                  {!isEditMode ? (
                    <>${reportDetail?.online_cancel}</>
                  ) : (
                    <Form.Item
                      name="online_cancel"
                      noStyle
                      rules={[
                        {
                          required: true,
                          message: "Please enter online cancel",
                        },
                      ]}
                    >
                      <Input
                        placeholder="Online cancel"
                        bordered={false}
                        value={reportDetail?.online_cancel}
                        style={{ padding: 0 }}
                      />
                    </Form.Item>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Reg Cash" span={3}>
                  $
                  {reportDetail?.instant_sales -
                    reportDetail?.online_cancel +
                    reportDetail?.online_sales -
                    reportDetail?.online_cashes -
                    reportDetail?.instant_cashes}
                </Descriptions.Item>
                <Descriptions.Item label="Activated today" span={3}>
                  {activatedToday(
                    reportDetail?.details ?? [],
                    reportDetail?.date,
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Sold today" span={3}>
                  {countMatches(reportDetail?.details ?? [], "is_sold", 1)}
                </Descriptions.Item>
                <Descriptions.Item label="Closed today" span={3}>
                  {countMatches(reportDetail?.details ?? [], "is_closed", 1)}
                </Descriptions.Item>
              </Descriptions>
              <Form.Item name="online_cancel" hidden={true}></Form.Item>
            </Form>
            <div style={{ flex: 1, overflowY: "auto", marginTop: 16 }}>
              <Table
                columns={endShiftColumns}
                dataSource={sortBySoldClosed(reportDetail?.details ?? [])}
                size="small"
                sticky
                tableLayout="auto"
                pagination={false}
              />
            </div>
          </div>
        )}
      </Drawer>

      {/* Expenses Table */}
      {/* <div className="app-surface rounded-lg shadow-sm border app-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="app-surface-alt border-b app-border">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium app-muted uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium app-muted uppercase tracking-wider">
                  Vehicle
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium app-muted uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium app-muted uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium app-muted uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium app-muted uppercase tracking-wider">
                  Receipt
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium app-muted uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="app-surface divide-y divide-gray-200">
              {expenses.map((expense) => (
                <tr key={expense.id} className="app-hover">
                  <td className="px-6 py-4 whitespace-nowrap text-sm app-text">
                    {new Date(expense.date).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm app-text">
                    <div className="flex items-center gap-2">
                      <Car className="w-4 h-4 app-muted" />
                      {expense.vehicle}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(
                        expense.category
                      )}`}
                    >
                      {getCategoryIcon(expense.category)}
                      {expense.category}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm app-text max-w-xs truncate">
                    {expense.description}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold app-text">
                    ${expense.amount.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm app-muted">
                    {expense.receipt ? (
                      <span className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 cursor-pointer">
                        View Receipt
                      </span>
                    ) : (
                      <span className="app-muted">No receipt</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm app-muted">
                    <div className="flex items-center gap-2">
                      <button
                        // onClick={() => handleEdit(expense)}
                        className="text-blue-600 hover:text-blue-900 transition-colors"
                      >
                        <Icon name="Edit2" className="w-4 h-4" />
                      </button>
                      <button
                        // onClick={() => handleDelete(expense.id)}
                        className="text-red-600 hover:text-red-900 transition-colors"
                      >
                        <Icon name="Trash2" className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div> */}

      {/* Add/Edit Modal */}
      {/* {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="app-surface rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold app-text mb-4">
                {editingExpense ? "Edit Expense" : "Add New Expense"}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium app-text mb-1">
                    Date
                  </label>
                  <input
                    type="date"
                    required
                    className="w-full px-3 py-2 border app-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.date}
                    onChange={(e) =>
                      setFormData({ ...formData, date: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium app-text mb-1">
                    Vehicle
                  </label>
                  <select
                    required
                    className="w-full px-3 py-2 border app-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.vehicle}
                    onChange={(e) =>
                      setFormData({ ...formData, vehicle: e.target.value })
                    }
                  >
                    <option value="">Select Vehicle</option>
                    {vehicles.map((vehicle) => (
                      <option key={vehicle} value={vehicle}>
                        {vehicle}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium app-text mb-1">
                    Category
                  </label>
                  <select
                    required
                    className="w-full px-3 py-2 border app-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.category}
                    onChange={(e) =>
                      setFormData({ ...formData, category: e.target.value })
                    }
                  >
                    <option value="">Select Category</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium app-text mb-1">
                    Description
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Enter expense description"
                    className="w-full px-3 py-2 border app-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium app-text mb-1">
                    Amount ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    placeholder="0.00"
                    className="w-full px-3 py-2 border app-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.amount}
                    onChange={(e) =>
                      setFormData({ ...formData, amount: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium app-text mb-1">
                    Receipt (optional)
                  </label>
                  <input
                    type="text"
                    placeholder="Receipt filename or reference"
                    className="w-full px-3 py-2 border app-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.receipt}
                    onChange={(e) =>
                      setFormData({ ...formData, receipt: e.target.value })
                    }
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 px-4 py-2 app-text app-surface-alt border app-border rounded-lg app-hover transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    {editingExpense ? "Update" : "Add"} Expense
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )} */}
    </section>
  );
};

const mapStateToProps = (state: RootState) => ({
  // Map state properties to props here if needed
  data: state.cms?.[PAGE_TYPE],
  storeId: state.user?.profileData?.storeId,
  REFRESH: state.levent?.[PAGE_TYPE],
});

const mapDispatchToProps = (dispatch: any) => ({
  // login: (data: any) => dispatch(getProfile(data)),
  fetchCms: (reqData: any) =>
    dispatch(fetchData(PATH_MANAGE, PATH, reqData, PAGE_TYPE)),
  generateToken: (id: any) =>
    dispatch(fetchData(PATH_MANAGE, `${PATH}/login/app/${id}`, {}, "")),
  createData: (reqData: any) =>
    dispatch(postData(PATH_MANAGE, `${PATH}`, reqData, PAGE_TYPE)),

  editData: (id: string, reqData: any) =>
    dispatch(putData(PATH_MANAGE, `${PATH}/`, id, reqData, PAGE_TYPE)),
  removeCms: (reqData: any) =>
    dispatch(deleteData(PATH_MANAGE, `${PATH}/`, reqData, PAGE_TYPE)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

// Define the props that connect will inject into the component
type PropsFromRedux = ConnectedProps<typeof connector>;

// Connect the component to Redux
export default connector(ConfigurationPage);
